import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { PAYMENT_MODE_DEFERRED_PAYOUT_WALLET, getWalletBalance } from '../../util/deferredPayout';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isStripePayoutReady } from '../../util/stripeConnect';

import IconClose from '../IconClose/IconClose';
import NamedLink from '../NamedLink/NamedLink';

import css from './PayoutDetailsBanner.module.css';

const sessionDismissKey = currentUser =>
  currentUser?.id?.uuid
    ? `PayoutDetailsBanner.dismissed.${currentUser.id.uuid}.${PAYMENT_MODE_DEFERRED_PAYOUT_WALLET}`
    : null;

export const shouldShowPayoutDetailsBanner = ({ currentUser, isProviderUserType }) => {
  if (!currentUser?.id || !isProviderUserType) {
    return false;
  }

  const walletBalance = getWalletBalance(currentUser);
  return walletBalance.amount > 0 && !isStripePayoutReady(currentUser);
};

/**
 * Topbar notification for providers who have deferred wallet balance and need Stripe payouts.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.currentUser
 * @param {boolean} props.isProviderUserType
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @returns {JSX.Element|null}
 */
const PayoutDetailsBanner = props => {
  const { currentUser, isProviderUserType, className, rootClassName } = props;
  const intl = useIntl();
  const key = sessionDismissKey(currentUser);
  const [dismissed, setDismissed] = useState(() => {
    return key && typeof window !== 'undefined'
      ? window.sessionStorage.getItem(key) === 'true'
      : false;
  });

  useEffect(() => {
    if (key && typeof window !== 'undefined') {
      setDismissed(window.sessionStorage.getItem(key) === 'true');
    }
  }, [key]);

  const shouldShow =
    !dismissed &&
    shouldShowPayoutDetailsBanner({
      currentUser,
      isProviderUserType,
    });

  const handleDismiss = () => {
    setDismissed(true);
    if (key && typeof window !== 'undefined') {
      window.sessionStorage.setItem(key, 'true');
    }
  };

  if (!shouldShow) {
    return null;
  }

  const closeLabel = intl.formatMessage({ id: 'PayoutDetailsBanner.dismiss' });
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.content}>
        <p className={css.message}>
          <FormattedMessage id="PayoutDetailsBanner.message" />
          <span className={css.ctaSeparator}> </span>
          <NamedLink className={css.cta} name="StripePayoutPage">
            <FormattedMessage id="PayoutDetailsBanner.cta" />
          </NamedLink>
        </p>
        <button
          type="button"
          className={css.dismissButton}
          onClick={handleDismiss}
          title={closeLabel}
          aria-label={closeLabel}
        >
          <IconClose rootClassName={css.closeIcon} size="small" />
        </button>
      </div>
    </div>
  );
};

export default PayoutDetailsBanner;
